import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const pillars = [
  { k: "Better connections", v: "Find the right partners, faster." },
  { k: "Greater transparency", v: "Visibility across every collaboration." },
  { k: "Technology that grows with you", v: "Built to help businesses grow together." },
];

const ECO_IMG =
  "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=1600&q=80";

export default function Ecosystem() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="ecosystem"
      ref={ref}
      className="relative overflow-hidden bg-[#0b1220] py-16 text-white sm:py-20"
      data-testid="ecosystem-section"
    >
      <motion.img
        src={ECO_IMG}
        alt="Connected city network"
        style={{ y }}
        className="absolute inset-0 h-[116%] w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b1220] via-[#0b1220]/70 to-[#0b1220]" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[500px] -translate-x-1/2 rounded-full bg-[#3b82f6]/20 blur-[130px]" />

      <div className="relative mx-auto max-w-5xl px-2.5 sm:px-6 text-center">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="eyebrow text-[#60a5fa]"
        >
          The Vision
        </motion.p>
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mx-auto mt-4 max-w-3xl text-3xl font-extrabold leading-[1.06] tracking-tight sm:text-4xl"
        >
          A more connected real estate ecosystem.
        </motion.h2>
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/70"
        >
          We believe the future of real estate will be built on better
          connections, greater transparency, and technology that helps
          businesses grow together. LintelGrid is building towards that future.
        </motion.p>

        <motion.div
          variants={stagger(0.1, 0.14)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3"
        >
          {pillars.map((p) => (
            <motion.div
              key={p.k}
              variants={fadeUp}
              className="rounded-[22px] border border-white/10 bg-white/[0.04] p-6 text-left backdrop-blur-sm transition-colors hover:border-white/25 hover:bg-white/[0.07]"
            >
              <h3 className="text-base font-bold tracking-tight text-white sm:text-lg">
                {p.k}
              </h3>
              <p className="mt-2 text-[13px] leading-relaxed text-white/60 sm:text-sm">{p.v}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
